import { useCallback, useContext, useEffect, useState } from 'react';
import { Box } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';

import { PageLoading } from '@vyce/core/src/components';
import { SettingsCard } from '@vyce/core/src/components/SettingsCard';
import { AppSwitch } from '@vyce/core/src/components/inputs';
import { TimeModuleContext } from '@vyce/core/src/contexts';
import { TimeAndAttendanceLocation } from '@vyce/core/src/types';

import { SnapshotList } from './components';
import { useScheduledNotifications, useStaffSearch } from './hooks';
import { useStyles } from './styles';
import { EMPTY_SNAPSHOT, SnapshotFormValue } from './types';

interface TimeNotificationsFormValues {
  dailySnapshots: SnapshotFormValue[];
  weeklySnapshots: SnapshotFormValue[];
}

interface Props {
  companyId?: string;
  locations?: TimeAndAttendanceLocation[];
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

export const TimeNotifications = (props: Props) => {
  const classes = useStyles();
  const { locations: contextLocations = [] } = useContext(TimeModuleContext);
  const locations = props.locations ?? contextLocations;

  const { staff, setSubstring: setStaffSubstring } = useStaffSearch({
    companyId: props.companyId,
  });

  const {
    loading,
    saving,
    togglingNotificationId,
    saveDailySnapshots,
    deleteDailySnapshot,
    toggleDailySnapshot,
    getDailyFormDefaults,
  } = useScheduledNotifications({
    companyId: props.companyId,
    locationIds: locations.map(l => l.uuid ?? l.name).filter(Boolean),
  });

  const methods = useForm<TimeNotificationsFormValues>({
    defaultValues: {
      dailySnapshots: [EMPTY_SNAPSHOT],
      weeklySnapshots: [EMPTY_SNAPSHOT],
    },
  });

  const savedSnapshots = (methods.watch('dailySnapshots') || []).filter(
    (snapshot: SnapshotFormValue) => !!snapshot?.notificationId
  );
  const activeCount = savedSnapshots.filter(
    (snapshot: SnapshotFormValue) => snapshot?.isActive !== false
  ).length;
  const allActive = savedSnapshots.length > 0 && activeCount === savedSnapshots.length;

  useEffect(() => {
    if (!loading) {
      const daily = getDailyFormDefaults();
      methods.reset({
        dailySnapshots: daily.length > 0 ? daily : [EMPTY_SNAPSHOT],
        weeklySnapshots: [EMPTY_SNAPSHOT],
      });
    }
  }, [loading]);

  const handleSave = useCallback(() => {
    const values = methods.getValues();
    saveDailySnapshots({
      dailySnapshots: values.dailySnapshots,
      locationsCount: locations.length,
    });
  }, [methods, saveDailySnapshots, locations.length]);

  const handleDelete = useCallback(
    (index: number) => {
      const snapshots = methods.getValues('dailySnapshots') || [];
      const notificationId = snapshots[index]?.notificationId;
      if (notificationId) deleteDailySnapshot(notificationId);
    },
    [methods, deleteDailySnapshot]
  );

  const handleToggleActive = useCallback(
    (index: number) => {
      const snapshots = methods.getValues('dailySnapshots') || [];
      const snapshot = snapshots[index];
      const notificationId = snapshot?.notificationId;
      if (!notificationId) return;

      const isActive = snapshot?.isActive ?? true;
      methods.setValue(`dailySnapshots.${index}.isActive`, !isActive);
      toggleDailySnapshot(notificationId, !isActive);
    },
    [methods, toggleDailySnapshot]
  );

  const handleToggleAllActive = useCallback(
    (activate: boolean) => {
      const snapshots = methods.getValues('dailySnapshots') || [];
      snapshots.forEach((snapshot: SnapshotFormValue, index: number) => {
        const notificationId = snapshot?.notificationId;
        if (notificationId) {
          methods.setValue(`dailySnapshots.${index}.isActive`, activate);
          toggleDailySnapshot(notificationId, activate);
        }
      });
    },
    [methods, toggleDailySnapshot]
  );

  const status = () => {
    if (!savedSnapshots.length) return 'Not set up yet';
    if (!activeCount) return 'Off';
    return `On · ${activeCount} ${activeCount === 1 ? 'email' : 'emails'}`;
  };

  return (
    <FormProvider {...methods}>
      <SettingsCard
        title="Snapshot Email"
        description="An email listing everyone clocked in today, plus anyone who didn’t clock out yesterday. Set up as many as you need — each one has its own locations, days, time and recipients."
        status={status()}
        statusTone={activeCount ? 'on' : 'off'}
        expanded={props.expanded}
        onExpandedChange={props.onExpandedChange}
        headerControl={
          <AppSwitch
            checked={allActive}
            disabled={!savedSnapshots.length}
            onChange={(_, checked) => handleToggleAllActive(checked)}
            inputProps={{ 'aria-label': 'Snapshot Email' }}
          />
        }
      >
        {loading ? (
          <Box height="100px">
            <PageLoading />
          </Box>
        ) : (
          <SnapshotList
            fieldArrayName="dailySnapshots"
            locations={locations}
            staff={staff}
            setStaffSubstring={setStaffSubstring}
            snapshotTitle="Snapshot"
            addButtonLabel="Add a Snapshot Email"
            defaultSnapshot={EMPTY_SNAPSHOT}
            boldClassName={classes.bold}
            onSave={handleSave}
            onDelete={handleDelete}
            onToggleActive={handleToggleActive}
            saving={saving}
            togglingNotificationId={togglingNotificationId}
          />
        )}
      </SettingsCard>
    </FormProvider>
  );
};
